import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useAuth} from './auth';
import {
  listarPropostas,
  listarEmprestimos,
  listarParcelasEmprestimo,
  PropostaItemAPI,
  EmprestimoItemAPI,
  ParcelaStatusVisual,
} from '../services/creditoMova';

export type EmprestimoAlertType = 'vencidas' | 'a_vencer' | 'assinatura' | null;

export type LoanWarning = {
  status: ParcelaStatusVisual;
  diasAtraso: number | null;
  valorCentavos: number;
  count: number;
};

type EmprestimoAlertsContextData = {
  alertType: EmprestimoAlertType;
  alertOperacaoId: string | null;
  alertParcelaValorCentavos: number | null;
  alertParcelaCount: number | null;
  loading: boolean;
  propostas: PropostaItemAPI[];
  emprestimos: EmprestimoItemAPI[];
  parcelasWarningMap: Record<string, LoanWarning | null>;
  loadData: () => Promise<void>;
};

const EmprestimoAlertsContext = createContext<EmprestimoAlertsContextData>(
  {} as EmprestimoAlertsContextData,
);

export function EmprestimoAlertsProvider({children}: {children: React.ReactNode}) {
  const {isLogged} = useAuth();

  const [loading, setLoading] = useState(false);
  const [propostas, setPropostas] = useState<PropostaItemAPI[]>([]);
  const [emprestimos, setEmprestimos] = useState<EmprestimoItemAPI[]>([]);
  const [parcelasWarningMap, setParcelasWarningMap] = useState<Record<string, LoanWarning | null>>({});
  const [alertType, setAlertType] = useState<EmprestimoAlertType>(null);
  const [alertOperacaoId, setAlertOperacaoId] = useState<string | null>(null);
  const [alertParcelaValorCentavos, setAlertParcelaValorCentavos] = useState<number | null>(null);
  const [alertParcelaCount, setAlertParcelaCount] = useState<number | null>(null);

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    if (!isLogged) {
      setAlertType(null);
      setAlertOperacaoId(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [propostasRes, emprestimosRes] = await Promise.all([
        listarPropostas().catch(() => ({items: []})),
        listarEmprestimos().catch(() => ({items: []})),
      ]);

      if (isStale()) {
        return;
      }

      setPropostas(propostasRes.items);
      setEmprestimos(emprestimosRes.items);

      const activeLoans = emprestimosRes.items.filter(i => i.status !== 'quitada');

      const parcelasResults = await Promise.all(
        activeLoans.map(loan =>
          listarParcelasEmprestimo(loan.operacao_id).catch(() => null),
        ),
      );

      if (isStale()) {
        return;
      }

      const map: Record<string, LoanWarning | null> = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      activeLoans.forEach((loan, idx) => {
        const res = parcelasResults[idx];
        if (!res) { map[loan.operacao_id] = null; return; }

        const atrasadas = res.items.filter(p => p.status_visual === 'em_atraso');
        if (atrasadas.length > 0) {
          const atrasada = atrasadas[0];
          const due = new Date(atrasada.data_vencimento.split('T')[0]);
          due.setHours(0, 0, 0, 0);
          const dias = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
          map[loan.operacao_id] = {status: 'em_atraso', diasAtraso: dias, valorCentavos: atrasada.valor_atualizado_centavos, count: atrasadas.length};
          return;
        }

        const vencem = res.items.filter(p => p.status_visual === 'vence_hoje');
        if (vencem.length > 0) {
          map[loan.operacao_id] = {status: 'vence_hoje', diasAtraso: null, valorCentavos: vencem[0].valor_atualizado_centavos, count: vencem.length};
          return;
        }

        const aVencerProximas = res.items.filter(p => {
          if (p.status_visual !== 'a_vencer') {return false;}
          const due = new Date(p.data_vencimento.split('T')[0]);
          due.setHours(0, 0, 0, 0);
          const diasRestantes = Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          return diasRestantes >= 0 && diasRestantes <= 7;
        });
        map[loan.operacao_id] = aVencerProximas.length > 0
          ? {status: 'a_vencer', diasAtraso: null, valorCentavos: aVencerProximas[0].valor_atualizado_centavos, count: aVencerProximas.length}
          : null;
      });

      setParcelasWarningMap(map);

      const vencidaEntry = Object.entries(map).find(([, w]) => w?.status === 'em_atraso');
      if (vencidaEntry) {
        setAlertType('vencidas');
        setAlertOperacaoId(vencidaEntry[0]);
        setAlertParcelaValorCentavos(vencidaEntry[1]?.valorCentavos ?? null);
        setAlertParcelaCount(vencidaEntry[1]?.count ?? null);
        return;
      }

      const aVencerEntry = Object.entries(map).find(([, w]) => w?.status === 'vence_hoje' || w?.status === 'a_vencer');
      if (aVencerEntry) {
        setAlertType('a_vencer');
        setAlertOperacaoId(aVencerEntry[0]);
        setAlertParcelaValorCentavos(aVencerEntry[1]?.valorCentavos ?? null);
        setAlertParcelaCount(aVencerEntry[1]?.count ?? null);
        return;
      }

      const temAssinatura = propostasRes.items.some(p => p.next_action === 'assinar');
      if (temAssinatura) {
        setAlertType('assinatura');
        setAlertOperacaoId(null);
        setAlertParcelaValorCentavos(null);
        setAlertParcelaCount(null);
        return;
      }

      setAlertType(null);
      setAlertOperacaoId(null);
      setAlertParcelaValorCentavos(null);
      setAlertParcelaCount(null);
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [isLogged]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(() => ({
    alertType,
    alertOperacaoId,
    alertParcelaValorCentavos,
    alertParcelaCount,
    loading,
    propostas,
    emprestimos,
    parcelasWarningMap,
    loadData: load,
  }),
  [
    alertType,
    alertOperacaoId,
    alertParcelaValorCentavos,
    alertParcelaCount,
    loading,
    propostas,
    emprestimos,
    parcelasWarningMap,
    load,
  ],
);

  return (
    <EmprestimoAlertsContext.Provider value={value}>
      {children}
    </EmprestimoAlertsContext.Provider>
  );
}

export function useEmprestimoAlerts() {
  return useContext(EmprestimoAlertsContext);
}